import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import {
  FiActivity, FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiChevronRight, FiClock, FiGrid, FiHelpCircle,
  FiList, FiLoader, FiMinusCircle, FiPlay, FiRefreshCw, FiX, FiZap,
} from "react-icons/fi";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtNum, fmtTime, useI18n, type Key } from "@/lib/i18n";
import {
  confidence, displayName, everyText, GROUP, integrationHealth, isCollector, kindOf, msText, posture, PROVIDES, STATE_RANK,
  type Health, type IntegrationRow, type IntState, type Kind, type RunMetrics,
} from "@/lib/integrations";
import { noteText, useIntegrations, useNow } from "@/components/MonitoringBanner";
import { durText } from "@/components/OntStatus";
import { cn } from "@/lib/utils";

type T = (k: Key, p?: Record<string, string | number>) => string;

// ---------- shared vocabulary ----------

const STATE_UI: Record<IntState, { icon: IconType; text: string; bg: string; ring: string }> = {
  healthy: { icon: FiCheckCircle, text: "text-signal-good", bg: "bg-signal-good/10", ring: "border-rule" },
  running: { icon: FiLoader, text: "text-signal", bg: "bg-signal/10", ring: "border-rule" },
  degraded: { icon: FiAlertTriangle, text: "text-signal-warn", bg: "bg-amber-400/10", ring: "border-amber-400/45" },
  stale: { icon: FiClock, text: "text-signal-warn", bg: "bg-amber-400/10", ring: "border-amber-400/45" },
  failed: { icon: FiAlertOctagon, text: "text-white", bg: "bg-signal-crit", ring: "border-signal-crit/60" },
  disabled: { icon: FiMinusCircle, text: "text-slate-400", bg: "bg-slate-700/40", ring: "border-rule" },
  unknown: { icon: FiHelpCircle, text: "text-slate-300", bg: "bg-slate-700/40", ring: "border-rule" },
};

function StateBadge({ state }: { state: IntState }) {
  const { t } = useI18n();
  const s = STATE_UI[state];
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold tracking-[0.12em] uppercase", s.text, s.bg)}>
      <s.icon className={cn("size-3", state === "running" && "animate-spin [animation-duration:2.5s]")} /> {t(`int.state.${state}` as Key)}
    </span>
  );
}

/** What one source object is called, per integration. */
const NOUN: Record<Kind, Key> = { olt: "int.noun.olts", snmp: "int.noun.olts", librenms: "int.noun.devices", genieacs: "int.noun.devices", whmcs: "int.noun.services", bras: "int.noun.routers", other: "int.noun.objects" };

/** Integration-specific metrics in one common shape: label, value, tone. */
function metricTiles(kind: Kind, m: RunMetrics | null | undefined, t: T, locale: string): { label: string; value: string; warn?: boolean }[] {
  if (!m) return [];
  const n = (v?: number) => fmtNum(locale, v ?? 0);
  const answered = { label: t(NOUN[kind]), value: `${n(m.total - m.failed)}/${n(m.total)}`, warn: m.failed > 0 };
  switch (kind) {
    case "olt": return [answered, { label: t("int.m.onts"), value: n(m.counts?.onts) }, { label: t("int.m.events"), value: n(m.counts?.events) }];
    case "snmp": return [answered];
    case "librenms": return [{ label: t("int.noun.devices"), value: n(m.total) }, { label: t("int.m.links"), value: n(m.counts?.links) }];
    case "genieacs": return [{ label: t("int.m.mirrored"), value: n(m.total) }, ...(m.failed ? [{ label: t("int.m.failed"), value: n(m.failed), warn: true }] : [])];
    case "whmcs": return [{ label: t("int.noun.services"), value: n(m.total) }, { label: t("int.m.linked"), value: n(m.counts?.linked) }];
    default: return [{ label: t("int.noun.objects"), value: n(m.total) }];
  }
}

/** Lower-case a noun for running text, but never an acronym ("OLTs"). */
const lc = (s: string) => (/^[A-Z]{2}/.test(s) ? s : s.toLowerCase());

function resultText(kind: Kind, m: RunMetrics | null | undefined, detail: string | null | undefined, t: T, locale: string) {
  if (!m) return detail ?? "—";
  return metricTiles(kind, m, t, locale).map((x) => `${x.value} ${lc(x.label)}`).join(" · ");
}

/** Exact time for the tooltip, relative age for scanning. */
function Ago({ iso, now, className }: { iso: string | null | undefined; now: number; className?: string }) {
  const { t, locale } = useI18n();
  if (!iso) return <span className={cn("text-slate-500", className)}>{t("int.never")}</span>;
  return <span className={cn("data", className)} title={fmtDateTime(locale, iso)}>{t("int.ago", { age: durText(now - Date.parse(iso)) })}</span>;
}

// ---------- manual jobs ----------

type Job = { op: "sync" | "test"; phase: "running" | "done" | "failed"; startedAt: number; endedAt?: number; text?: string };

function useJobs() {
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [jobs, setJobs] = useState<Record<string, Job>>({});
  const set = (name: string, j: Job | null) => setJobs((m) => {
    const next = { ...m };
    if (j) next[name] = j; else delete next[name];
    return next;
  });
  const start = async (name: string, op: Job["op"]) => {
    const startedAt = Date.now();
    set(name, { op, phase: "running", startedAt });
    try {
      if (op === "test") {
        const r = await api<{ ok: boolean; info: string }>(`/api/v1/integrations/${encodeURIComponent(name)}/test`, { method: "POST" });
        set(name, { op, phase: r.ok ? "done" : "failed", startedAt, endedAt: Date.now(), text: r.ok ? t("int.job.verified") : r.info });
      } else {
        const r = await api<{ detail?: string; run?: { metrics?: RunMetrics | null; detail?: string | null } }>(
          `/api/v1/integrations/${encodeURIComponent(name)}/sync`, { method: "POST" });
        const m = r.run?.metrics;
        const text = m ? [resultText(kindOf(name), m, null, t, locale), t("int.job.failedN", { n: m.failed })].join(" · ") : r.detail ?? r.run?.detail ?? "";
        set(name, { op, phase: "done", startedAt, endedAt: Date.now(), text });
      }
    } catch (e) {
      set(name, { op, phase: "failed", startedAt, endedAt: Date.now(), text: (e as Error).message });
    }
    qc.invalidateQueries({ queryKey: ["integrations"] });
    qc.invalidateQueries({ queryKey: ["int-runs", name] });
  };
  return { jobs, start, dismiss: (name: string) => set(name, null) };
}

type Jobs = ReturnType<typeof useJobs>;

// ---------- page ----------

type Item = { row: IntegrationRow; kind: Kind; h: Health };
const ORDER: Kind[] = ["olt", "snmp", "librenms", "genieacs", "whmcs", "other"];

export default function IntegrationsPage() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const canAct = user?.role === "admin";
  const q = useIntegrations();
  const now = useNow();
  const jobs = useJobs();
  const [open, setOpen] = useState<string | null>(null);
  const [view, setView] = useState<"cards" | "table" | null>(null);

  const items: Item[] = useMemo(() => (q.data?.integrations ?? []).map((row) => ({ row, kind: kindOf(row.name), h: integrationHealth(row, now) }))
    .sort((a, b) => STATE_RANK[a.h.state] - STATE_RANK[b.h.state] || ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind) || a.row.name.localeCompare(b.row.name)),
  [q.data, now]);
  const { posture: p, counts } = posture(items.map((i) => i.h));
  const conf = q.data ? confidence(q.data.integrations, now) : null;
  const mode = view ?? (items.length > 12 ? "table" : "cards");
  // Groups only pay for themselves once there are more cards than one glance holds.
  const groups = items.length > 6
    ? (["collectors", "observability", "acs", "billing", "other"] as const)
      .map((g) => ({ g, items: items.filter((i) => GROUP[i.kind] === g) })).filter((x) => x.items.length)
    : [{ g: null, items }];
  const openItem = items.find((i) => i.row.name === open) ?? null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("nav.integrations")}</h1>
          <p className="mt-1 text-[12.5px] text-slate-400">{t("int.subtitle")}</p>
        </div>
        <div className="flex items-center gap-1 rounded-sm border border-rule p-0.5">
          {([["cards", FiGrid], ["table", FiList]] as const).map(([v, I]) => (
            <button key={v} type="button" onClick={() => setView(v)} aria-pressed={mode === v}
              className={cn("inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-[12px]", mode === v ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>
              <I className="size-3.5" /> {t(`int.view.${v}` as Key)}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading && <p className="text-slate-400">{t("common.loading")}</p>}
      {q.error && <p className="text-signal-crit">{(q.error as Error).message}</p>}

      {q.data && conf && (
        <PostureStrip p={p} counts={counts} conf={conf} items={items} now={now} generatedAt={q.data.generated_at} />
      )}

      {q.data && groups.map(({ g, items: gi }) => (
        <section key={g ?? "all"} className="space-y-2">
          {g && <h2 className="eyebrow pt-1">{t(`int.group.${g}` as Key)} <span className="data text-slate-600">{gi.length}</span></h2>}
          {mode === "cards" ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {gi.map((i) => <IntegrationCard key={i.row.name} item={i} now={now} jobs={jobs} canAct={canAct} onOpen={() => setOpen(i.row.name)} />)}
            </div>
          ) : (
            <IntegrationTable items={gi} now={now} jobs={jobs} canAct={canAct} onOpen={setOpen} locale={locale} />
          )}
        </section>
      ))}

      {openItem && <Drawer item={openItem} now={now} jobs={jobs} canAct={canAct} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ---------- posture + confidence ----------

const POSTURE_UI = {
  operational: { icon: FiCheckCircle, text: "text-signal-good", key: "int.posture.operational" },
  degraded: { icon: FiAlertTriangle, text: "text-signal-warn", key: "int.posture.degraded" },
  failure: { icon: FiAlertOctagon, text: "text-signal-crit", key: "int.posture.failure" },
  unknown: { icon: FiHelpCircle, text: "text-slate-400", key: "int.posture.unknown" },
} as const;

function PostureStrip({ p, counts, conf, items, now, generatedAt }: {
  p: keyof typeof POSTURE_UI; counts: Record<IntState, number>; conf: ReturnType<typeof confidence>; items: Item[]; now: number; generatedAt: string;
}) {
  const { t, locale } = useI18n();
  const P = POSTURE_UI[p];
  const live = items.length - counts.disabled;
  const chip = (s: IntState, n: number) => (
    <span key={s} className={cn("inline-flex items-center gap-1.5", n ? STATE_UI[s].text.replace("text-white", "text-signal-crit") : "text-slate-600")}>
      <span className="data text-[15px] font-semibold">{fmtNum(locale, n)}</span>
      <span className="text-[12px]">{t(`int.state.${s}` as Key)}</span>
    </span>
  );
  // Freshness of what the network picture is built on, not of this page.
  const fresh = items.filter((i) => i.kind === "olt" || i.kind === "snmp");
  const lowConf = conf.level === "low";
  return (
    <div className={cn("grid gap-px overflow-hidden rounded-lg border bg-rule lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]",
      p === "failure" ? "border-signal-crit/50" : p === "degraded" ? "border-amber-400/40" : "border-rule")}>
      <div className="flex flex-col justify-center gap-2 bg-panel px-5 py-3">
        <div className={cn("flex items-center gap-2 text-[14px] font-semibold tracking-[0.14em] uppercase", P.text)}>
          <P.icon className="size-4" /> {t(P.key)}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
          <span className="data text-[12.5px] text-slate-400">{t("int.healthyOf", { n: fmtNum(locale, counts.healthy + counts.running), total: fmtNum(locale, live) })}</span>
          {chip("degraded", counts.degraded)}{chip("stale", counts.stale)}{chip("failed", counts.failed)}
          {counts.disabled > 0 && chip("disabled", counts.disabled)}
        </div>
        <div className="flex flex-wrap gap-x-4 text-[12px] text-slate-500">
          {fresh.map((i) => (
            <span key={i.row.name}>{t(`int.fresh.${i.kind}` as Key)}{" "}
              <Ago iso={i.row.last_ok_at} now={now} className={cn(i.h.state === "stale" || i.h.state === "failed" ? "text-signal-warn" : "text-slate-300")} />
            </span>
          ))}
          <span title={fmtDateTime(locale, generatedAt)}>{t("int.checked", { age: durText(now - Date.parse(generatedAt)) })}</span>
        </div>
      </div>
      <div className={cn("bg-panel px-5 py-3", lowConf ? "bg-[linear-gradient(90deg,rgba(239,68,68,0.12),transparent_70%)]" : conf.level === "degraded" && "bg-[linear-gradient(90deg,rgba(245,158,11,0.08),transparent_70%)]")}>
        <div className="flex items-center gap-2">
          <span className="eyebrow">{t("int.conf.title")}</span>
          <span className={cn("inline-flex items-center gap-1.5 text-[12px] font-semibold tracking-[0.12em] uppercase",
            conf.level === "good" ? "text-signal-good" : lowConf ? "text-signal-crit" : "text-signal-warn")}>
            <span className={cn("size-1.5 rounded-full", conf.level === "good" ? "bg-signal-good" : lowConf ? "bg-signal-crit" : "bg-signal-warn")} />
            {t(`int.conf.${conf.level}` as Key)}
          </span>
        </div>
        {conf.notes.length === 0 ? (
          <p className="mt-1.5 text-[13px] text-slate-300">{t("int.conf.allFresh")}</p>
        ) : (
          <ul className="mt-1.5 space-y-0.5 text-[13px] text-slate-100">
            {conf.notes.map((n) => <li key={n.key}>{noteText(n, t)}</li>)}
          </ul>
        )}
        {lowConf && <p className="mt-1 text-[12.5px] font-semibold text-rose-200">{t("int.banner.notDown")}</p>}
        {conf.side.length > 0 && (
          <p className="mt-1 text-[12px] text-slate-400">{conf.side.map((n) => noteText(n, t)).join("  ·  ")}</p>
        )}
      </div>
    </div>
  );
}

// ---------- card ----------

function Spark({ recent, className }: { recent: IntegrationRow["stats"]["recent"]; className?: string }) {
  const { locale } = useI18n();
  if (!recent.length) return <div className={cn("h-5", className)} />;
  const max = Math.max(...recent.map((r) => r.ms), 1);
  return (
    <div className={cn("flex h-5 items-end gap-[2px]", className)} aria-hidden>
      {recent.map((r, i) => (
        <span key={i} title={`${fmtTime(locale, r.at)} · ${msText(r.ms)} · ${r.s}`}
          className={cn("w-full min-w-[2px] rounded-[1px]", r.s === "error" ? "bg-signal-crit" : r.s === "disabled" ? "bg-slate-700" : "bg-slate-500/60")}
          style={{ height: r.s === "error" ? "100%" : `${Math.max(12, (r.ms / max) * 100)}%` }} />
      ))}
    </div>
  );
}

/** The one explanation of a non-healthy state, shared by card and drawer. */
function Problem({ item, now, full }: { item: Item; now: number; full?: boolean }) {
  const { t, locale } = useI18n();
  const { row, kind, h } = item;
  const m = row.last_run.metrics;
  const noun = lc(t(NOUN[kind]));
  const tag: Key | null = h.cause === "source" || h.cause === "partial" ? "int.tag.source"
    : h.cause === "collector" ? "int.tag.collector" : h.cause === "external" ? "int.tag.external" : null;
  let line = "";
  switch (h.cause) {
    case "stale": line = h.sinceOkMs === null ? t("int.why.staleNever", { every: everyText(h.intervalMs) }) : t("int.why.stale", { age: durText(h.sinceOkMs), every: everyText(h.intervalMs) }); break;
    case "collector": line = t("int.why.collector", { detail: row.last_run.detail ?? "—" }); break;
    case "external": line = t("int.why.external", { detail: row.last_run.detail ?? "—" }); break;
    case "source": line = t("int.why.source", { n: fmtNum(locale, m?.total ?? 0), noun }); break;
    case "partial": line = t("int.why.partial", { n: fmtNum(locale, m?.failed ?? 0), total: fmtNum(locale, m?.total ?? 0), noun }); break;
    case "warnings": line = t("int.why.warnings", { w: m?.warnings?.[0] ?? "" }); break;
    case "slow": line = t("int.why.slow", { ms: msText(h.lastMs), p50: msText(row.stats.p50_24h_ms) }); break;
    case "never": line = t("int.why.never"); break;
  }
  const failures = (m?.failures ?? []).slice(0, full ? 20 : 2);
  const more = (m?.failures?.length ?? 0) - failures.length;
  // A source failure IS a network signal; everything else is about our view of it.
  const notNetwork = h.cause === "stale" || h.cause === "collector" || h.cause === "external";
  return (
    <div className="space-y-1 text-[12.5px]">
      <p className="text-slate-100">
        {tag && <span className={cn("mr-1.5 rounded-sm border px-1 py-px text-[10px] font-semibold tracking-[0.12em] uppercase",
          tag === "int.tag.source" ? "border-amber-400/50 text-amber-200" : "border-signal-crit/50 text-rose-200")}>{t(tag)}</span>}
        {line}
      </p>
      {failures.length > 0 && (
        <ul className="data space-y-px text-[11.5px] text-slate-400">
          {failures.map((f) => <li key={f} className="truncate" title={f}>· {f}</li>)}
          {more > 0 && <li className="text-slate-500">{t("int.more", { n: more })}</li>}
        </ul>
      )}
      {h.state !== "degraded" || h.cause === "partial" ? (
        <p className="text-slate-400">
          {t("int.affected")}: <span className="text-slate-200">{PROVIDES[kind].map((p) => t(`int.provides.${p}` as Key)).join(" · ")}</span>
          {notNetwork && isCollector(kind) && <span className="text-slate-500"> — {t("int.mayBeFine")}</span>}
        </p>
      ) : null}
      {full && h.sinceOkMs !== null && h.state !== "healthy" && (
        <p className="text-slate-500">{t("int.lastOk")} <Ago iso={row.last_ok_at} now={now} className="text-slate-300" /></p>
      )}
    </div>
  );
}

function JobStrip({ item, job, now, onDismiss }: { item: Item; job: Job | undefined; now: number; onDismiss: () => void }) {
  const { t, locale } = useI18n();
  const { h, row } = item;
  const typical = row.stats.p50_24h_ms;
  if (job?.phase === "running" || (!job && h.running)) {
    const since = job ? job.startedAt : Date.parse(row.running_since!);
    const el = now - since;
    // Real progress when the job reports it; otherwise elapsed against the
    // usual duration, capped below 100% -- never an invented count.
    const pr = row.progress && row.progress.total > 0 ? row.progress : null;
    const frac = pr ? pr.done / pr.total : typical ? Math.min(0.95, el / typical) : null;
    return (
      <div className="rounded-sm border border-signal/30 bg-signal/[0.06] px-2.5 py-1.5" aria-live="polite">
        <div className="flex items-center gap-2 text-[12px]">
          <FiRefreshCw className="size-3.5 animate-spin text-signal [animation-duration:1.6s]" />
          <span className="font-semibold tracking-[0.12em] text-signal uppercase">
            {t(job ? (job.op === "test" ? "int.job.testing" : "int.job.syncing") : "int.job.scheduled")}
          </span>
          <span className="data ml-auto text-slate-300">{msText(el)}{!pr && typical ? <span className="text-slate-500"> / ~{msText(typical)}</span> : null}</span>
        </div>
        {pr && (
          <p className="data mt-1 text-[12px] text-slate-300">
            {t(`int.stage.${pr.stage}` as Key, { done: fmtNum(locale, pr.done), total: fmtNum(locale, pr.total) })}
            <span className="text-slate-500"> · {Math.floor((pr.done / pr.total) * 100)}%</span>
          </p>
        )}
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-slate-800">
          {frac !== null ? <div className="h-full bg-signal transition-[width] duration-1000" style={{ width: `${frac * 100}%` }} />
            : <div className="h-full w-1/3 animate-pulse bg-signal/70" />}
        </div>
      </div>
    );
  }
  if (!job) return null;
  const ok = job.phase === "done";
  return (
    <div className={cn("flex items-start gap-2 rounded-sm border px-2.5 py-1.5 text-[12px]", ok ? "border-signal-good/30 bg-signal-good/[0.06]" : "border-signal-crit/40 bg-signal-crit/10")} aria-live="polite">
      {ok ? <FiCheckCircle className="mt-0.5 size-3.5 shrink-0 text-signal-good" /> : <FiAlertOctagon className="mt-0.5 size-3.5 shrink-0 text-signal-crit" />}
      <div className="min-w-0">
        <span className={cn("font-semibold tracking-[0.12em] uppercase", ok ? "text-signal-good" : "text-rose-200")}>
          {t(`int.job.${job.op}.${ok ? "ok" : "fail"}` as Key)}
        </span>
        <span className="data text-slate-500"> · {msText((job.endedAt ?? now) - job.startedAt)}</span>
        {job.text && <p className="mt-0.5 break-words text-slate-300">{job.text}</p>}
      </div>
      <button type="button" onClick={onDismiss} aria-label={t("common.close")} className="ml-auto text-slate-500 hover:text-slate-200"><FiX /></button>
    </div>
  );
}

function Actions({ item, jobs, canAct, compact }: { item: Item; jobs: Jobs; canAct: boolean; compact?: boolean }) {
  const { t } = useI18n();
  if (!canAct) return null;
  const { row, kind, h } = item;
  const busy = h.running || jobs.jobs[row.name]?.phase === "running";
  const run = isCollector(kind);
  const btn = "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm border border-rule px-2 py-1 text-[12px] text-slate-200 hover:border-signal/50 hover:text-signal disabled:cursor-not-allowed disabled:opacity-40";
  const title = busy ? t("int.busy") : undefined;
  return (
    <>
      {!run && kind !== "other" && (
        <button type="button" className={btn} disabled={busy || h.state === "disabled"} title={title ?? (compact ? t("int.act.test") : undefined)} aria-label={t("int.act.test")} onClick={() => jobs.start(row.name, "test")}>
          <FiZap className="size-3.5" /> {!compact && t("int.act.test")}
        </button>
      )}
      <button type="button" className={btn} disabled={busy || h.state === "disabled"} title={title ?? (compact ? t(run ? "int.act.run" : "int.act.sync") : undefined)} aria-label={t(run ? "int.act.run" : "int.act.sync")} onClick={() => jobs.start(row.name, "sync")}>
        {run ? <FiPlay className="size-3.5" /> : <FiRefreshCw className="size-3.5" />}
        {!compact && t(h.state === "failed" ? "int.act.retry" : run ? "int.act.run" : "int.act.sync")}
      </button>
    </>
  );
}

function IntegrationCard({ item, now, jobs, canAct, onOpen }: { item: Item; now: number; jobs: Jobs; canAct: boolean; onOpen: () => void }) {
  const { t, locale } = useI18n();
  const { row, kind, h } = item;
  const s = STATE_UI[h.state];
  const problem = !["healthy", "running", "disabled"].includes(h.state);
  const tiles = metricTiles(kind, row.last_run.metrics, t, locale);
  const job = jobs.jobs[row.name];
  const st = row.stats;
  return (
    <article className={cn("flex min-h-[232px] flex-col rounded-lg border bg-panel", s.ring,
      h.state === "failed" && "bg-[linear-gradient(180deg,rgba(239,68,68,0.09),transparent_45%)]",
      (h.state === "stale" || h.state === "degraded") && "bg-[linear-gradient(180deg,rgba(245,158,11,0.07),transparent_45%)]")}>
      <header className="flex items-center gap-2 border-b border-rule/70 px-4 py-2.5">
        <button type="button" onClick={onOpen} className="min-w-0 truncate text-left text-[13px] font-semibold tracking-[0.08em] text-slate-100 uppercase hover:text-signal">
          {displayName(row.name)}
        </button>
        <span className="hidden text-[10.5px] tracking-[0.12em] text-slate-500 uppercase sm:inline">{t(isCollector(kind) ? "int.kind.collector" : "int.kind.external")}</span>
        <span className="ml-auto"><StateBadge state={h.state} /></span>
      </header>

      <div className="flex flex-1 flex-col gap-2.5 px-4 py-3">
        {h.state === "disabled" ? (
          <p className="text-[12.5px] text-slate-400">{t("int.disabled")}{row.last_run.detail ? <span className="data text-slate-500"> · {row.last_run.detail}</span> : null}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[12.5px] text-slate-400">
              <span>{t("int.lastOk")} <Ago iso={row.last_ok_at} now={now} className={cn(h.state === "stale" || h.state === "failed" ? "font-semibold text-signal-warn" : "text-slate-100")} /></span>
              <span>{t("int.every", { every: everyText(h.intervalMs) })}</span>
              <span>{t("int.took")} <span className={cn("data", h.cause === "slow" ? "text-signal-warn" : "text-slate-200")}>{msText(h.lastMs)}</span></span>
              {row.last_failure && (
                <span>{t("int.lastFail")} <Ago iso={row.last_failure.finished_at} now={now} className="text-slate-300" /></span>
              )}
            </div>

            {tiles.length > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {tiles.map((x) => (
                  <div key={x.label}>
                    <div className="eyebrow text-[10px]">{x.label}</div>
                    <div className={cn("data text-[17px] leading-tight", x.warn ? "text-signal-warn" : "text-slate-100")}>{x.value}</div>
                  </div>
                ))}
                <div className="ml-auto text-right">
                  <div className="eyebrow text-[10px]">{t("int.reliability24")}</div>
                  <div className={cn("data text-[17px] leading-tight", st.ok_24h < st.runs_24h ? "text-signal-warn" : "text-slate-100")}>
                    {st.runs_24h ? `${fmtNum(locale, st.ok_24h)}/${fmtNum(locale, st.runs_24h)}` : "—"}
                  </div>
                </div>
              </div>
            )}
            {!tiles.length && !problem && row.last_run.detail && <p className="data truncate text-[12px] text-slate-400" title={row.last_run.detail}>{row.last_run.detail}</p>}

            {(job || h.running) ? <JobStrip item={item} job={job} now={now} onDismiss={() => jobs.dismiss(row.name)} />
              : problem ? <Problem item={item} now={now} />
              : <p className="truncate text-[12px] text-slate-500">{t("int.provides")}: {PROVIDES[kind].map((p) => t(`int.provides.${p}` as Key)).join(" · ")}</p>}
          </>
        )}

        <footer className="mt-auto flex items-center gap-2 pt-1">
          <Actions item={item} jobs={jobs} canAct={canAct} />
          <Spark recent={h.state === "disabled" ? [] : st.recent} className="mx-2 min-w-0 max-w-40 flex-1" />
          <button type="button" onClick={onOpen} className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[12px] text-slate-400 hover:text-signal">
            {t(problem ? "int.investigate" : "int.details")} <FiChevronRight />
          </button>
        </footer>
      </div>
    </article>
  );
}

// ---------- table (10-20+ integrations) ----------

const COL_W = ["w-[17%]", "w-[11%]", "w-[10%]", "w-[7%]", "w-[7%]", "", "w-[8%]", "w-[10%]"];

function IntegrationTable({ items, now, jobs, canAct, onOpen, locale }: { items: Item[]; now: number; jobs: Jobs; canAct: boolean; onOpen: (n: string) => void; locale: string }) {
  const { t } = useI18n();
  const cols: Key[] = ["int.col.integration", "int.col.state", "int.col.lastOk", "int.col.every", "int.col.duration", "int.col.result", "int.reliability24", "int.col.trend"];
  return (
    <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
      <table className="w-full min-w-[1100px] table-fixed text-[13px]">
        <thead>
          <tr className="border-b border-rule text-left">
            {/* Fixed widths: every group's table lines up with the others. */}
            {cols.map((k, i) => <th key={k} className={cn("eyebrow px-3 py-2 font-medium", COL_W[i])}>{t(k)}</th>)}
            <th className="w-32" />
          </tr>
        </thead>
        <tbody>
          {items.map((i) => {
            const { row, kind, h } = i;
            const job = jobs.jobs[row.name];
            return (
              <tr key={row.name} className={cn("border-b border-rule/70 last:border-b-0 hover:bg-white/[0.02]", h.state === "failed" && "bg-signal-crit/[0.06]", (h.state === "stale" || h.state === "degraded") && "bg-amber-400/[0.04]")}>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => onOpen(row.name)} className="font-medium text-slate-100 hover:text-signal">{displayName(row.name)}</button>
                  <div className="text-[11px] text-slate-500">{t(isCollector(kind) ? "int.kind.collector" : "int.kind.external")}</div>
                </td>
                <td className="px-3 py-2">
                  <StateBadge state={h.state} />
                  {job?.phase === "running" && <span className="ml-2 text-[11.5px] text-signal">{t("int.job.syncing")}</span>}
                </td>
                <td className="px-3 py-2"><Ago iso={row.last_ok_at} now={now} className={cn(h.state === "stale" || h.state === "failed" ? "text-signal-warn" : "text-slate-200")} /></td>
                <td className="data px-3 py-2 text-slate-400">{everyText(h.intervalMs)}</td>
                <td className={cn("data px-3 py-2", h.cause === "slow" ? "text-signal-warn" : "text-slate-300")}>{msText(h.lastMs)}</td>
                <td className="max-w-80 truncate px-3 py-2 text-slate-300" title={row.last_run.detail ?? ""}>
                  {row.last_run.status === "error" ? <span className="text-rose-300">{row.last_run.detail}</span> : resultText(kind, row.last_run.metrics, row.last_run.detail, t, locale)}
                </td>
                <td className="data px-3 py-2 text-slate-300">{row.stats.runs_24h ? `${row.stats.ok_24h}/${row.stats.runs_24h}` : "—"}</td>
                <td className="w-36 px-3 py-2"><Spark recent={row.stats.recent} /></td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1.5">
                    <Actions item={i} jobs={jobs} canAct={canAct} compact />
                    <button type="button" onClick={() => onOpen(row.name)} aria-label={t("int.details")} className="px-1 text-slate-500 hover:text-signal"><FiChevronRight /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- detail drawer ----------

type RunsResponse = { runs: { id: number; status: string; detail: string | null; started_at: string; finished_at: string; metrics: RunMetrics | null }[] };
const TABS = ["overview", "history", "failures", "performance", "config"] as const;

function Drawer({ item, now, jobs, canAct, onClose }: { item: Item; now: number; jobs: Jobs; canAct: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<(typeof TABS)[number]>("overview");
  const { row, h } = item;
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" onClick={onClose}>
      <aside role="dialog" aria-modal aria-label={displayName(row.name)} onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full max-w-[680px] flex-col border-l border-rule bg-[#070b14] shadow-2xl">
        <header className="border-b border-rule px-5 pt-4">
          <div className="flex items-center gap-3">
            <h2 className="text-[16px] font-semibold tracking-[0.08em] text-slate-100 uppercase">{displayName(row.name)}</h2>
            <StateBadge state={h.state} />
            <div className="ml-auto flex items-center gap-1.5">
              <Actions item={item} jobs={jobs} canAct={canAct} />
              <button type="button" onClick={onClose} aria-label={t("common.close")} className="p-1 text-slate-400 hover:text-slate-100"><FiX className="size-4" /></button>
            </div>
          </div>
          <p className="data mt-1 text-[12px] text-slate-500">{row.endpoint || t(isCollector(item.kind) ? "int.cfg.fromOlts" : "int.cfg.noEndpoint")}</p>
          <nav className="mt-3 flex gap-4">
            {TABS.map((x) => (
              <button key={x} type="button" onClick={() => setTab(x)}
                className={cn("-mb-px border-b-2 pb-2 text-[12.5px]", tab === x ? "border-signal text-slate-100" : "border-transparent text-slate-400 hover:text-slate-100")}>
                {t(`int.tab.${x}` as Key)}
              </button>
            ))}
          </nav>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {tab === "overview" && <OverviewTab item={item} now={now} jobs={jobs} />}
          {tab === "history" && <HistoryTab item={item} />}
          {tab === "failures" && <FailuresTab item={item} now={now} />}
          {tab === "performance" && <PerformanceTab item={item} />}
          {tab === "config" && <ConfigTab item={item} />}
        </div>
      </aside>
    </div>
  );
}

function KV({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_minmax(0,1fr)] gap-3 border-b border-rule/60 py-1.5 text-[13px] last:border-b-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="min-w-0 text-slate-200">{children}</dd>
    </div>
  );
}

function OverviewTab({ item, now, jobs }: { item: Item; now: number; jobs: Jobs }) {
  const { t, locale } = useI18n();
  const { row, kind, h } = item;
  const st = row.stats;
  const m = row.last_run.metrics;
  const problem = !["healthy", "running", "disabled"].includes(h.state);
  return (
    <div className="space-y-4">
      {(jobs.jobs[row.name] || h.running) && <JobStrip item={item} job={jobs.jobs[row.name]} now={now} onDismiss={() => jobs.dismiss(row.name)} />}
      {problem && <div className={cn("rounded-md border p-3", STATE_UI[h.state].ring)}><Problem item={item} now={now} full /></div>}
      <section>
        <h3 className="eyebrow mb-1">{t("int.sec.freshness")}</h3>
        <dl>
          <KV label={t("int.lastRun")}>
            <span className={cn("mr-2 text-[11px] font-semibold uppercase", row.last_run.status === "error" ? "text-signal-crit" : "text-slate-400")}>{row.last_run.status}</span>
            <Ago iso={row.last_run.finished_at} now={now} /> <span className="data text-slate-500">· {msText(h.lastMs)}</span>
          </KV>
          <KV label={t("int.lastOk")}><Ago iso={row.last_ok_at} now={now} /></KV>
          <KV label={t("int.lastFail")}>
            {row.last_failure ? <><Ago iso={row.last_failure.finished_at} now={now} /><p className="data mt-0.5 break-words text-[12px] text-rose-300">{row.last_failure.detail}</p></> : <span className="text-slate-500">{t("int.none24")}</span>}
          </KV>
          <KV label={t("int.col.every")}>{everyText(h.intervalMs)} <span className="text-slate-500">· {t("int.staleAfter", { w: everyText(h.windowMs) })}</span></KV>
        </dl>
      </section>
      <section>
        <h3 className="eyebrow mb-1">{t("int.sec.result")}</h3>
        <dl>
          {m ? metricTiles(kind, m, t, locale).map((x) => <KV key={x.label} label={x.label}><span className={cn("data", x.warn && "text-signal-warn")}>{x.value}</span></KV>)
            : <KV label={t("int.col.result")}>{row.last_run.detail ?? "—"}</KV>}
          {m?.warnings?.map((w) => <KV key={w} label={t("int.warning")}><span className="text-amber-200">{w}</span></KV>)}
        </dl>
      </section>
      <section>
        <h3 className="eyebrow mb-1">{t("int.sec.reliability")}</h3>
        <dl>
          <KV label={t("int.w1h")}><span className="data">{st.runs_1h ? t("int.okOf", { ok: fmtNum(locale, st.ok_1h), n: fmtNum(locale, st.runs_1h) }) : "—"}</span></KV>
          <KV label={t("int.w24h")}><span className="data">{st.runs_24h ? t("int.okOf", { ok: fmtNum(locale, st.ok_24h), n: fmtNum(locale, st.runs_24h) }) : "—"}</span></KV>
        </dl>
      </section>
      <section>
        <h3 className="eyebrow mb-1">{t("int.provides")}</h3>
        <p className="text-[13px] text-slate-300">{PROVIDES[kind].map((p) => t(`int.provides.${p}` as Key)).join(" · ")}</p>
      </section>
    </div>
  );
}

function useRuns(name: string, status?: string) {
  return useQuery({
    queryKey: ["int-runs", name, status ?? "all"],
    queryFn: () => api<RunsResponse>(`/api/v1/integrations/${encodeURIComponent(name)}/runs?limit=50${status ? `&status=${status}` : ""}`),
    refetchInterval: 30_000,
  });
}

function HistoryTab({ item }: { item: Item }) {
  const { t, locale } = useI18n();
  const q = useRuns(item.row.name);
  if (q.isLoading) return <p className="text-slate-400">{t("common.loading")}</p>;
  const runs = q.data?.runs ?? [];
  if (!runs.length) return <p className="text-slate-500">{t("integrations.noRuns")}</p>;
  return (
    <table className="w-full text-[12.5px]">
      <thead><tr className="border-b border-rule text-left">
        {(["int.col.state", "int.col.started", "int.col.duration", "int.col.result"] as Key[]).map((k) => <th key={k} className="eyebrow py-1.5 pr-3 font-medium">{t(k)}</th>)}
      </tr></thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.id} className="border-b border-rule/60">
            <td className="py-1.5 pr-3">
              <span className={cn("inline-flex items-center gap-1.5", r.status === "error" ? "text-signal-crit" : r.status === "ok" ? (r.metrics?.failed ? "text-signal-warn" : "text-signal-good") : "text-slate-500")}>
                <span className="size-1.5 rounded-full bg-current" />{t(`int.run.${r.status === "ok" && r.metrics?.failed ? "partial" : r.status}` as Key)}
              </span>
            </td>
            <td className="data py-1.5 pr-3 text-slate-300" title={fmtDateTime(locale, r.started_at)}>{fmtTime(locale, r.started_at)}</td>
            <td className="data py-1.5 pr-3 text-slate-300">{msText(Date.parse(r.finished_at) - Date.parse(r.started_at))}</td>
            <td className={cn("max-w-72 truncate py-1.5", r.status === "error" ? "text-rose-300" : "text-slate-400")} title={r.detail ?? ""}>
              {r.status === "error" ? r.detail : resultText(item.kind, r.metrics, r.detail, t, locale)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FailuresTab({ item, now }: { item: Item; now: number }) {
  const { t } = useI18n();
  const q = useRuns(item.row.name, "error");
  const m = item.row.last_run.metrics;
  return (
    <div className="space-y-4">
      <section>
        <h3 className="eyebrow mb-1.5">{t("int.sec.sourceFailures")}</h3>
        {m?.failures?.length ? (
          <ul className="data space-y-1 text-[12.5px] text-slate-200">
            {m.failures.map((f) => <li key={f} className="rounded-sm border border-rule/70 bg-panel px-2 py-1 break-words">{f}</li>)}
          </ul>
        ) : <p className="text-[13px] text-slate-500">{t("int.noSourceFailures")}</p>}
      </section>
      <section>
        <h3 className="eyebrow mb-1.5">{t("int.sec.failedRuns")}</h3>
        {q.isLoading ? <p className="text-slate-400">{t("common.loading")}</p> : !q.data?.runs.length ? (
          <p className="text-[13px] text-slate-500">{t("int.noFailedRuns")}</p>
        ) : (
          <ul className="space-y-1.5">
            {q.data.runs.map((r) => (
              <li key={r.id} className="rounded-sm border border-signal-crit/30 bg-signal-crit/[0.05] px-2.5 py-1.5 text-[12.5px]">
                <Ago iso={r.finished_at} now={now} className="text-slate-300" />
                <p className="data mt-0.5 break-words text-rose-200">{r.detail}</p>
                {r.metrics?.failures?.slice(0, 5).map((f) => <p key={f} className="data truncate text-[11.5px] text-slate-400">· {f}</p>)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PerformanceTab({ item }: { item: Item }) {
  const { t, locale } = useI18n();
  const q = useRuns(item.row.name);
  const st = item.row.stats;
  const runs = [...(q.data?.runs ?? [])].reverse();
  const ds = runs.map((r) => Date.parse(r.finished_at) - Date.parse(r.started_at));
  const max = Math.max(...ds, st.p95_24h_ms ?? 0, 1);
  const y = (ms: number) => 100 - (ms / max) * 100;
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-4 gap-3">
        {([["int.perf.last", item.h.lastMs], ["int.perf.avg1h", st.avg_1h_ms], ["int.perf.p50", st.p50_24h_ms], ["int.perf.p95", st.p95_24h_ms]] as [Key, number | null][]).map(([k, v]) => (
          <div key={k} className="rounded-sm border border-rule bg-panel px-3 py-2">
            <dt className="eyebrow text-[10px]">{t(k)}</dt>
            <dd className="data text-[18px] text-slate-100">{msText(v)}</dd>
          </div>
        ))}
      </dl>
      <section>
        <h3 className="eyebrow mb-1.5 flex gap-3">{t("int.perf.chart", { n: fmtNum(locale, runs.length) })}
          <span className="ml-auto text-signal normal-case">— p50</span><span className="text-signal-warn normal-case">— p95</span></h3>
        <div className="h-40 rounded-sm border border-rule bg-panel p-2">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
            {st.p95_24h_ms !== null && <line x1="0" x2="100" y1={y(st.p95_24h_ms)} y2={y(st.p95_24h_ms)} stroke="#f59e0b" strokeDasharray="1.5 1.5" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />}
            {st.p50_24h_ms !== null && <line x1="0" x2="100" y1={y(st.p50_24h_ms)} y2={y(st.p50_24h_ms)} stroke="#22d3ee" strokeDasharray="1.5 1.5" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />}
            {ds.map((d, i) => {
              const w = 100 / Math.max(ds.length, 1);
              return <rect key={i} x={i * w + w * 0.15} width={w * 0.7} y={y(d)} height={100 - y(d)} fill={runs[i].status === "error" ? "#ef4444" : "#64748b"} />;
            })}
          </svg>
        </div>
        <p className="mt-1.5 text-[12px] text-slate-500">{t("int.perf.slowRule")}</p>
      </section>
    </div>
  );
}

const CRED: Record<Kind, Key> = { olt: "int.cfg.cred.olt", snmp: "int.cfg.cred.snmp", librenms: "int.cfg.cred.env", genieacs: "int.cfg.cred.acs", whmcs: "int.cfg.cred.env", bras: "int.cfg.cred.bras", other: "int.cfg.cred.env" };

function ConfigTab({ item }: { item: Item }) {
  const { t } = useI18n();
  const { row, kind, h } = item;
  return (
    <div className="space-y-3">
      <dl>
        <KV label={t("int.cfg.enabled")}>{h.state === "disabled" ? <span className="text-slate-400">{t("int.cfg.notConfigured")}</span> : t("int.cfg.yes")}</KV>
        <KV label={t("int.col.every")}><span className="data">{everyText(h.intervalMs)}</span>{!row.interval_s && <span className="text-slate-500"> · {t("int.cfg.defaultInterval")}</span>}</KV>
        <KV label={t("int.cfg.stale")}><span className="data">{everyText(h.windowMs)}</span></KV>
        <KV label={t("int.cfg.source")}><span className="data">{row.endpoint || t(isCollector(kind) ? "int.cfg.fromOlts" : "int.cfg.noEndpoint")}</span></KV>
        {kind === "snmp" && <KV label={t("int.cfg.timeout")}><span className="data">10s · 1 retry · v2c</span></KV>}
        <KV label={t("int.cfg.credentials")}>{t(CRED[kind])}</KV>
      </dl>
      <p className="flex items-start gap-2 text-[12px] text-slate-500"><FiActivity className="mt-0.5 shrink-0" />{t("int.cfg.note")}</p>
    </div>
  );
}
